(function() {
  'use strict';

  const productCache = new Map();
  let isApplyingProgrammatically = false;

  async function getProductData(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (e) {
      return null;
    }
  }

  function getProductHandle(picker) {
    const fromData = picker.dataset.productUrl ||
                     picker.closest('[data-product-handle]')?.dataset.productHandle;
    if (fromData) return fromData.replace(/.*\/products\//, '').split('?')[0];
    const fromUrl = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    return fromUrl ? fromUrl[1] : null;
  }

  function getOptionGroups(picker) {
    const radios = picker.querySelectorAll('input[type="radio"]');
    const groups = {};
    radios.forEach(radio => {
      if (!groups[radio.name]) groups[radio.name] = [];
      groups[radio.name].push(radio);
    });
    return groups;
  }

  function getOptionName(radioName) {
    return radioName.split('-')[0];
  }

  function getCurrentSelection(picker) {
    const groups = getOptionGroups(picker);
    const selection = {};
    Object.keys(groups).forEach(groupName => {
      const checked = groups[groupName].find(r => r.checked);
      if (checked) selection[getOptionName(groupName)] = checked.value;
    });
    return selection;
  }

  function findBestVariant(productData, forced, previousSelection) {
    if (!productData?.variants) return null;
    const optionNames = productData.options.map(o => typeof o === 'string' ? o : o.name);

    const hasValue = (variant, optName, value) => {
      const idx = optionNames.indexOf(optName);
      return idx !== -1 && variant[`option${idx + 1}`] === value;
    };

    const candidates = productData.variants.filter(v =>
      hasValue(v, forced.name, forced.value)
    );
    if (candidates.length === 0) return null;

    const score = (v) => {
      let s = v.available ? 1000 : 0;
      Object.keys(previousSelection).forEach(optName => {
        if (optName === forced.name) return;
        if (hasValue(v, optName, previousSelection[optName])) s += 1;
      });
      return s;
    };

    candidates.sort((a, b) => score(b) - score(a));
    return candidates[0];
  }

  function applyVariant(picker, productData, variant) {
    const optionNames = productData.options.map(o => typeof o === 'string' ? o : o.name);
    const groups = getOptionGroups(picker);

    isApplyingProgrammatically = true;

    try {
      optionNames.forEach((optName, idx) => {
        const value = variant[`option${idx + 1}`];
        if (!value) return;
        const groupName = Object.keys(groups).find(g => getOptionName(g) === optName);
        if (!groupName) return;
        const radio = groups[groupName].find(r => r.value === value);
        if (!radio || radio.checked) return;
        radio.click();
      });
    } finally {
      setTimeout(() => { isApplyingProgrammatically = false; }, 100);
    }
  }

  function handleClick(e, picker, productData) {
    if (isApplyingProgrammatically) return;

    const radio = e.target.closest('input[type="radio"]');
    if (!radio || !picker.contains(radio)) return;
    if (radio.hasAttribute('data-variant-id')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!productData) return;

    const optionName = getOptionName(radio.name);
    const previousSelection = getCurrentSelection(picker);
    const best = findBestVariant(productData,
      { name: optionName, value: radio.value },
      previousSelection
    );

    if (best) applyVariant(picker, productData, best);
  }

  async function bindPicker(picker) {
    if (picker.dataset.autoSelectBound === 'true') return;
    picker.dataset.autoSelectBound = 'true';

    const handle = getProductHandle(picker);
    if (!handle) return;

    const productData = await getProductData(handle);
    if (!productData) return;

    picker.addEventListener('click', (e) => handleClick(e, picker, productData), true);
  }

  function init() {
    document.querySelectorAll('variant-picker, .variant-picker').forEach(bindPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();